import { Banner, Button, DefinitionList, Modal, notify, Section, StatusBadge, StatusPill, TextField, Toggle, useConfirm } from "@flexfreight/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
import { IntegrationBody } from "./integrationParts";

/* Admin Center > Integrations: sign-in code providers (Resend, Twilio) and EagleLoad (rating and booking). Saved keys are never
 * sent back to the browser; the API returns only whether one is saved (and the last four of a SID). */

type LastTest = { at: string; ok: boolean | null; message: string | null } | null;
export type ResendView = {
  from: string;
  keySaved: boolean;
  configured: boolean;
  enabled: boolean;
  /** "Use Resend for customer emails" (takes effect only while Resend is on). */
  customerEmail?: boolean;
  /** "Allow sign-in by email code" (default on; takes effect only while Resend is on). */
  signinCode?: boolean;
  lastTest: LastTest;
  status: "Awaiting credentials" | "Off" | "Connected";
};
export type TwilioView = {
  accountSidLast4: string | null;
  tokenSaved: boolean;
  sender: { kind: "number" | "messaging_service"; value: string } | null;
  configured: boolean;
  enabled: boolean;
  /** "Allow sign-in by text code" (default on; takes effect only while Twilio is on). */
  signinCode?: boolean;
  lastTest: LastTest;
  status: "Awaiting credentials" | "Off" | "Connected";
  /** SMS-pumping guard rails. */
  allowedCountries: string[];
  perHour: number;
  perDay: number;
};
export type EagleLoadView = {
  keySaved: boolean;
  webhookSecretSaved: boolean;
  username: string | null;
  accountId: string | null;
  enabled: boolean;
  booking: "off" | "test" | "live";
  status: string;
  lastTest: LastTest;
  webhookPath: string;
};

const when = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
export const testLine = (t: LastTest) => (t ? `${when.format(new Date(t.at))}: ${t.message ?? (t.ok ? "Passed" : "Failed")}` : "Never");
const RESEND_KEY = ["console", "integrations", "resend"];
const TWILIO_KEY = ["console", "integrations", "twilio"];
const EAGLE_KEY = ["console", "integrations", "eagleload"];

export function ResendCard({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: RESEND_KEY, queryFn: () => api<ResendView>("/v1/console/integrations/resend") });
  const v = q.data;
  const [key, setKey] = useState("");
  const [from, setFrom] = useState<string | null>(null);
  const fromValue = from ?? (v?.from || "FlexFreight <no-reply@flexfreight.net>");
  const set = (r: ResendView) => qc.setQueryData(RESEND_KEY, r);
  const save = useMutation({
    mutationFn: (p: { enabled: boolean; customerEmail?: boolean; signinCode?: boolean }) =>
      api<ResendView>("/v1/console/integrations/resend", { method: "PUT", body: { ...(key.trim() ? { apiKey: key.trim() } : {}), from: fromValue.trim(), ...p } }),
    onSuccess: (r, p) => {
      set(r);
      setKey("");
      setFrom(null);
      // Invites, resets and the "email isn't connected" notices follow this setting.
      void qc.invalidateQueries({ queryKey: ["console", "email-status"] });
      if (p.customerEmail !== undefined) notify.success(r.customerEmail ? "Customer emails go through Resend" : "Customer emails no longer use Resend");
      else if (p.signinCode !== undefined) notify.success(r.signinCode === false ? "Sign-in by email code is off" : "Sign-in by email code is on");
      else notify.success(r.enabled ? "Email codes are on" : "Resend settings saved");
    },
    onError: (e) => notify.error("Couldn't save", message(e)),
  });
  const test = useMutation({
    mutationFn: () => api<ResendView>("/v1/console/integrations/resend/test", { method: "POST" }),
    onSuccess: (r) => {
      set(r);
      if (r.lastTest?.ok) notify.success("Test email sent", r.lastTest.message ?? undefined);
      else notify.error("Test didn't send", r.lastTest?.message ?? undefined);
    },
    onError: (e) => notify.error("Couldn't test", message(e)),
  });
  return (
    <Section
      title="Sign-in codes by email (Resend)"
      description="Sends two-step sign-in codes by email. It can also send customer email (invites, password resets, notifications) when you choose that below. Marketing email never uses it."
      actions={v ? <StatusBadge domain="integration" value={v.status} /> : null}
    >
      <IntegrationBody q={q} canManage={canManage}>
        {v ? (
          <>
            <DefinitionList
              items={[
                { term: "API key", detail: v.keySaved ? "Saved" : "Missing" },
                { term: "From", detail: v.from || "Not set" },
                { term: "Last test", detail: testLine(v.lastTest) },
              ]}
            />
            {canManage ? (
              <>
                <form
                  className="ffa-inlineform"
                  autoComplete="off"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (key.trim() || v.keySaved) save.mutate({ enabled: v.enabled });
                  }}
                >
                  <TextField label="Resend API key" type="password" autoComplete="new-password" spellCheck={false} value={key} onChange={(e) => setKey(e.target.value)} hint={v.keySaved ? "Saved. Leave blank to keep it." : "Starts with re_"} />
                  <TextField label="From address" spellCheck={false} value={fromValue} onChange={(e) => setFrom(e.target.value)} />
                  <Button type="submit" size="lg" variant="primary" disabled={!key.trim() && !v.keySaved} loading={save.isPending}>Save</Button>
                </form>
                <div className="ffa-btnrow">
                  <Toggle look="switch" label="Email codes on" checked={v.enabled} disabled={!v.configured || save.isPending} onChange={(on) => save.mutate({ enabled: on })} />
                  <Button disabled={!v.configured} loading={test.isPending} onClick={() => test.mutate()}>Send test to me</Button>
                </div>
                <Toggle
                  look="switch"
                  label="Allow sign-in by email code"
                  checked={v.signinCode !== false}
                  disabled={!v.enabled || save.isPending}
                  onChange={(on) => save.mutate({ enabled: v.enabled, signinCode: on })}
                  description={
                    !v.enabled
                      ? "Turn email codes on first. Then staff can sign in with a code emailed to them instead of a password."
                      : "On: the sign-in page offers \"Email me a sign-in code\". Owners and admins still need their authenticator or a text code afterwards while the two-step policy below is on."
                  }
                />
                <Toggle
                  look="switch"
                  label="Use Resend for customer emails (invites, password resets, notifications)"
                  checked={!!v.customerEmail}
                  disabled={!v.configured || save.isPending}
                  onChange={(on) => save.mutate({ enabled: v.enabled, customerEmail: on })}
                  description={
                    v.customerEmail && !v.enabled
                      ? "Chosen, but Resend is off: customer email uses the server's default until you turn Resend on."
                      : "Off by default. When on (and Resend is on), welcome, invite, password reset and shipment emails to customers go from this address."
                  }
                />
              </>
            ) : null}
          </>
        ) : null}
      </IntegrationBody>
    </Section>
  );
}

export function TwilioCard({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: TWILIO_KEY, queryFn: () => api<TwilioView>("/v1/console/integrations/twilio") });
  const v = q.data;
  const [sid, setSid] = useState("");
  const [token, setToken] = useState("");
  const [sender, setSender] = useState("");
  const [to, setTo] = useState("");
  const [countries, setCountries] = useState<string | null>(null);
  const [perHour, setPerHour] = useState<string | null>(null);
  const [perDay, setPerDay] = useState<string | null>(null);
  const set = (r: TwilioView) => qc.setQueryData(TWILIO_KEY, r);
  const countryList = (countries ?? v?.allowedCountries.join(", ") ?? "1").split(/[\s,+]+/).filter(Boolean);
  const limitsOk = countryList.length > 0 && countryList.every((c) => /^\d{1,3}$/.test(c)) && [perHour, perDay].every((x) => x === null || /^\d{1,5}$/.test(x.trim()));
  const senderBody = () => {
    const s = sender.trim();
    if (!s) return {};
    return /^MG/i.test(s) ? { messagingServiceSid: s } : { from: s };
  };
  const save = useMutation({
    mutationFn: (p: { enabled: boolean; signinCode?: boolean }) =>
      api<TwilioView>("/v1/console/integrations/twilio", {
        method: "PUT",
        body: {
          ...(sid.trim() ? { accountSid: sid.trim() } : {}),
          ...(token.trim() ? { authToken: token.trim() } : {}),
          ...senderBody(),
          ...(countries !== null ? { allowedCountries: countryList } : {}),
          ...(perHour !== null ? { perHour: Number(perHour) } : {}),
          ...(perDay !== null ? { perDay: Number(perDay) } : {}),
          ...p,
        },
      }),
    onSuccess: (r, p) => {
      set(r);
      setSid("");
      setToken("");
      setSender("");
      setCountries(null);
      setPerHour(null);
      setPerDay(null);
      if (p.signinCode !== undefined) notify.success(r.signinCode === false ? "Sign-in by text code is off" : "Sign-in by text code is on");
      else notify.success(r.enabled ? "Text codes are on" : "Twilio settings saved");
    },
    onError: (e) => notify.error("Couldn't save", message(e)),
  });
  const test = useMutation({
    mutationFn: () => api<TwilioView>("/v1/console/integrations/twilio/test", { method: "POST", body: { to: to.trim() } }),
    onSuccess: (r) => {
      set(r);
      if (r.lastTest?.ok) notify.success("Test text sent", r.lastTest.message ?? undefined);
      else notify.error("Test didn't send", r.lastTest?.message ?? undefined);
    },
    onError: (e) => notify.error("Couldn't test", message(e)),
  });
  const dirty = !!(sid.trim() || token.trim() || sender.trim()) || countries !== null || perHour !== null || perDay !== null;
  return (
    <Section title="Sign-in codes by text (Twilio)" description="Sends two-step sign-in codes by text message. Used only for sign-in codes." actions={v ? <StatusBadge domain="integration" value={v.status} /> : null}>
      <IntegrationBody q={q} canManage={canManage}>
        {v ? (
          <>
            <DefinitionList
              items={[
                { term: "Account", detail: <span className="ffx-num">{v.accountSidLast4 ?? "Not set"}</span> },
                { term: "Auth token", detail: v.tokenSaved ? "Saved" : "Missing" },
                { term: "Sends from", detail: <span className="ffx-num">{v.sender ? (v.sender.kind === "number" ? v.sender.value : `Messaging service ${v.sender.value}`) : "Not set"}</span> },
                { term: "Allowed countries", detail: <span className="ffx-num">{v.allowedCountries.map((c) => `+${c}`).join(", ")}</span> },
                { term: "Sending limit", detail: <span className="ffx-num">{`${v.perHour} an hour, ${v.perDay} a day (all users)`}</span> },
                { term: "Last test", detail: testLine(v.lastTest) },
              ]}
            />
            {canManage ? (
              <>
                <form
                  className="ffa-integration-form"
                  autoComplete="off"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (dirty && limitsOk) save.mutate({ enabled: v.enabled });
                  }}
                >
                  <div className="ffx-fields">
                    <TextField label="Account SID" type="password" autoComplete="new-password" spellCheck={false} value={sid} onChange={(e) => setSid(e.target.value)} hint={v.accountSidLast4 ? "Saved. Leave blank to keep it." : "Starts with AC"} />
                    <TextField label="Auth token" type="password" autoComplete="new-password" spellCheck={false} value={token} onChange={(e) => setToken(e.target.value)} hint={v.tokenSaved ? "Saved. Leave blank to keep it." : undefined} />
                    <TextField label="Sending number or messaging service SID" spellCheck={false} value={sender} onChange={(e) => setSender(e.target.value)} hint={v.sender ? "Leave blank to keep it." : "+15551234567 or MG..."} />
                    <TextField label="Allowed country codes" spellCheck={false} inputMode="numeric" value={countries ?? v.allowedCountries.join(", ")} onChange={(e) => setCountries(e.target.value)} hint="Comma separated, e.g. 1 for US and Canada" error={countries !== null && !limitsOk ? "Use 1 to 3 digit country codes" : undefined} />
                    <TextField label="Texts per hour" inputMode="numeric" value={perHour ?? String(v.perHour)} onChange={(e) => setPerHour(e.target.value)} />
                    <TextField label="Texts per day" inputMode="numeric" value={perDay ?? String(v.perDay)} onChange={(e) => setPerDay(e.target.value)} />
                  </div>
                  <div className="ffa-btnrow">
                    <Toggle look="switch" label="Text codes on" checked={v.enabled} disabled={!v.configured || save.isPending} onChange={(on) => save.mutate({ enabled: on })} />
                    <Button type="submit" variant="primary" disabled={!dirty || !limitsOk} loading={save.isPending}>Save</Button>
                  </div>
                </form>
                <Toggle
                  look="switch"
                  label="Allow sign-in by text code"
                  checked={v.signinCode !== false}
                  disabled={!v.enabled || save.isPending}
                  onChange={(on) => save.mutate({ enabled: v.enabled, signinCode: on })}
                  description={
                    !v.enabled
                      ? "Turn text codes on first. Then staff with a verified mobile on file can sign in with a texted code instead of a password."
                      : "On: the sign-in page offers \"Text me a sign-in code\". Texts go only to the verified mobile on the staff account, and a text code counts as the strong factor (owners and admins are then fully signed in)."
                  }
                />
                <form
                  className="ffa-inlineform"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (v.configured && to.replace(/\D/g, "").length >= 10) test.mutate();
                  }}
                >
                  <TextField label="Test number" type="tel" inputMode="tel" autoComplete="off" value={to} onChange={(e) => setTo(e.target.value)} />
                  <Button type="submit" size="lg" disabled={!v.configured || to.replace(/\D/g, "").length < 10} loading={test.isPending}>Send test text</Button>
                </form>
              </>
            ) : null}
          </>
        ) : null}
      </IntegrationBody>
    </Section>
  );
}

export type EagleLoadBooking = "off" | "test" | "live";
const BOOKING_OPTIONS: { value: EagleLoadBooking; label: string }[] = [
  { value: "off", label: "Off (quote only)" },
  { value: "test", label: "Test (manual dispatch)" },
  { value: "live", label: "Live (sends pickup requests)" },
];
const EAGLE_LIVE_PHRASE = "GO LIVE EAGLELOAD";

/**
 * EagleLoad: FlexFreight's rating and booking platform. With it on, quotes in the portal and the quoting center rate
 * through EagleLoad. Booking stays off until an admin picks test (manual dispatch) or live (real pickup requests).
 */
export function EagleLoadCard({ canManage, canGoLive = canManage }: { canManage: boolean; canGoLive?: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: EAGLE_KEY, queryFn: () => api<EagleLoadView>("/v1/console/integrations/eagleload") });
  const v = q.data;
  const [key, setKey] = useState("");
  const [secret, setSecret] = useState("");
  const [user, setUser] = useState<string | null>(null);
  const [account, setAccount] = useState<string | null>(null);
  const [goLive, setGoLive] = useState(false);
  const [typed, setTyped] = useState("");
  const { confirm, dialog } = useConfirm();
  const set = (r: EagleLoadView) => qc.setQueryData(EAGLE_KEY, r);
  const save = useMutation({
    mutationFn: (enabled: boolean) =>
      api<EagleLoadView>("/v1/console/integrations/eagleload", {
        method: "PUT",
        body: { ...(key.trim() ? { apiKey: key.trim() } : {}), ...(secret.trim() ? { webhookSecret: secret.trim() } : {}), username: (user ?? v?.username ?? "").trim(), accountId: (account ?? v?.accountId ?? "").trim(), enabled },
      }),
    onSuccess: (r) => {
      set(r);
      setKey("");
      setSecret("");
      setUser(null);
      setAccount(null);
      notify.success(r.enabled ? "EagleLoad is on" : "EagleLoad saved");
    },
    onError: (e) => notify.error("Couldn't save", message(e)),
  });
  const test = useMutation({
    mutationFn: () => api<EagleLoadView>("/v1/console/integrations/eagleload/test", { method: "POST" }),
    onSuccess: (r) => {
      set(r);
      if (r.lastTest?.ok) notify.success("EagleLoad connected", r.lastTest.message ?? undefined);
      else notify.error("Test didn't pass", r.lastTest?.message ?? undefined);
    },
    onError: (e) => notify.error("Couldn't test", message(e)),
  });
  const booking = useMutation({
    mutationFn: (mode: EagleLoadBooking) => api<EagleLoadView>("/v1/console/integrations/eagleload/booking", { method: "PATCH", body: { mode, ...(mode === "live" ? { confirm: typed } : {}) } }),
    onSuccess: (r) => {
      set(r);
      setGoLive(false);
      setTyped("");
      notify.success(r.booking === "live" ? "EagleLoad booking is live" : r.booking === "test" ? "EagleLoad test booking is on" : "EagleLoad booking is off", r.booking === "live" ? "Bookings send real pickup requests to carriers." : undefined);
    },
    onError: (e) => notify.error("Booking setting not changed", message(e)),
  });
  const clear = useMutation({
    mutationFn: () => api<EagleLoadView>("/v1/console/integrations/eagleload", { method: "DELETE" }),
    onSuccess: (r) => {
      set(r);
      notify.success("EagleLoad key removed");
    },
    onError: (e) => notify.error("Couldn't remove", message(e)),
  });
  const askClear = async () => {
    if (await confirm({ title: "Remove the EagleLoad key?", body: "Quotes stop rating through EagleLoad until a new key is saved. Booking settings stay as they are.", confirmLabel: "Remove key", danger: true })) clear.mutate();
  };
  // The badge reads the state; the API's full sentence is in Status below.
  const state = !v?.keySaved ? (v?.status ?? "Awaiting credentials") : v.enabled ? "On" : "Key saved";
  return (
    <Section title="EagleLoad" description="Real carrier rates through EagleLoad for the portal and the quoting center. FlexFreight's markup is added on top of EagleLoad's all-in price." actions={v ? <StatusBadge domain="integration" value={state} /> : null}>
      <IntegrationBody q={q} canManage={canManage}>
        {v ? (
          <>
            <DefinitionList
              items={[
                { term: "Status", detail: v.status },
                { term: "API key", detail: v.keySaved ? "Saved" : "Missing" },
                { term: "Booking", detail: BOOKING_OPTIONS.find((o) => o.value === v.booking)?.label ?? "Off" },
                { term: "Last test", detail: testLine(v.lastTest) },
                { term: "Webhooks", detail: v.webhookSecretSaved ? `Signing secret saved. EagleLoad posts to ${v.webhookPath} on the FlexFreight API.` : `No signing secret yet (tracking still updates by polling). Webhook address: ${v.webhookPath} on the FlexFreight API.` },
                { term: "Username", detail: v.username ?? "Not set" },
                { term: "Account ID", detail: <span className="ffx-num">{v.accountId ?? "Not set"}</span> },
              ]}
            />
            {canManage ? (
              <>
                <form
                  className="ffa-integration-form"
                  autoComplete="off"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (key.trim() || v.keySaved) save.mutate(v.enabled);
                  }}
                >
                  <div className="ffx-fields">
                    <TextField label="EagleLoad API key" type="password" autoComplete="new-password" spellCheck={false} value={key} onChange={(e) => setKey(e.target.value)} hint={v.keySaved ? "Saved. Leave blank to keep it." : "A personal key (EagleLoad Settings). The company key can't create quotes."} />
                    <TextField label="Webhook signing secret" optional type="password" autoComplete="new-password" spellCheck={false} value={secret} onChange={(e) => setSecret(e.target.value)} hint={v.webhookSecretSaved ? "Saved. Leave blank to keep it." : undefined} />
                    <TextField label="Username" optional spellCheck={false} value={user ?? v.username ?? ""} onChange={(e) => setUser(e.target.value)} />
                    <TextField label="Account or company ID" optional spellCheck={false} value={account ?? v.accountId ?? ""} onChange={(e) => setAccount(e.target.value)} />
                  </div>
                  <div className="ffa-btnrow">
                    <Toggle look="switch" label="Quote through EagleLoad" checked={v.enabled} disabled={!v.keySaved || save.isPending} onChange={(on) => save.mutate(on)} />
                    <Button disabled={!v.keySaved} loading={test.isPending} onClick={() => test.mutate()}>Test connection</Button>
                    {v.keySaved ? <Button variant="ghost" loading={clear.isPending} onClick={() => void askClear()}>Remove key</Button> : null}
                    <Button type="submit" variant="primary" disabled={!key.trim() && !v.keySaved} loading={save.isPending}>Save</Button>
                  </div>
                </form>
                <div className="ffa-btnrow" role="group" aria-label="EagleLoad booking">
                  <span className="ffx-micro">Booking</span>
                  {BOOKING_OPTIONS.map((o) => (
                    <Button
                      key={o.value}
                      size="sm"
                      variant={v.booking === o.value ? "primary" : "ghost"}
                      aria-pressed={v.booking === o.value}
                      disabled={!v.keySaved || booking.isPending || (o.value === "live" && !canGoLive)}
                      onClick={() => (o.value === "live" ? setGoLive(v.booking !== "live") : v.booking !== o.value && booking.mutate(o.value))}
                    >
                      {o.label}
                    </Button>
                  ))}
                </div>
              </>
            ) : null}
          </>
        ) : null}
      </IntegrationBody>
      <Modal
        open={goLive}
        onClose={() => {
          setGoLive(false);
          setTyped("");
        }}
        size="sm"
        title="Turn on live EagleLoad booking?"
        description="Live booking sends a real pickup request to the carrier for every EagleLoad booking. It needs a passing test."
        footer={
          <>
            <Button variant="ghost" onClick={() => setGoLive(false)}>Cancel</Button>
            <Button variant="destructive" disabled={typed !== EAGLE_LIVE_PHRASE} loading={booking.isPending} onClick={() => booking.mutate("live")}>Go live</Button>
          </>
        }
      >
        <TextField label={`Type ${EAGLE_LIVE_PHRASE} to confirm`} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} />
      </Modal>
      {dialog}
    </Section>
  );
}

export type AuthPolicyView = { requirePrivilegedTwoStep: boolean; canChange: boolean; sessionStrong: boolean };
const POLICY_KEY = ["console", "auth-policy"];

/**
 * "Require two-step for owners and admins": owner-only. Off means an owner/admin can reach the console with a
 * password or an email code alone. Turning it off needs a session verified by an authenticator or text code, a
 * confirmation, and is audited (auth.policy.changed). A warning banner shows while it is off.
 */
export function SigninPolicyCard() {
  const me = useConsoleMe();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: POLICY_KEY, queryFn: () => api<AuthPolicyView>("/v1/console/auth-policy") });
  const { confirm, dialog } = useConfirm();
  const v = q.data;
  const save = useMutation({
    mutationFn: (requirePrivilegedTwoStep: boolean) => api<AuthPolicyView>("/v1/console/auth-policy", { method: "PUT", body: { requirePrivilegedTwoStep } }),
    onSuccess: (r) => {
      qc.setQueryData(POLICY_KEY, r);
      // The gate re-reads the policy: an email-only owner session may now be outside it.
      void qc.invalidateQueries({ queryKey: ["console-session"] });
      notify.success(r.requirePrivilegedTwoStep ? "Two-step is required for owners and admins" : "Two-step is no longer required for owners and admins");
    },
    onError: (e) => notify.error("Policy not changed", e instanceof ApiError && e.reason === "step_up_required" ? "Sign in with your authenticator app or a text code first." : message(e)),
  });
  async function flip(on: boolean) {
    if (on) return save.mutate(true);
    const ok = await confirm({
      title: "Turn off two-step for owners and admins?",
      body: "Owners and admins could then reach the Admin Center with a password or an emailed code alone. An inbox that receives password resets would be enough to take over an owner account. This is recorded in the audit log.",
      confirmLabel: "Turn off two-step",
      danger: true,
    });
    if (ok) save.mutate(false);
  }
  const isOwner = me.role === "owner";
  const off = v ? !v.requirePrivilegedTwoStep : false;
  return (
    <Section
      title="Two-step policy"
      description="Who must use an authenticator app or text codes for the admin console. Owner only."
      actions={v ? <StatusPill tone={off ? "warning" : "success"}>{off ? "Off" : "Required"}</StatusPill> : null}
    >
      <IntegrationBody q={q} canManage>
        {v ? (
          <>
            {off ? (
              <Banner tone="warning" title="Two-step is off for owners and admins">
                A password or an emailed code alone now reaches the Admin Center. Turn it back on unless you have a reason not to.
              </Banner>
            ) : null}
            <Toggle
              look="switch"
              label="Require two-step for owners and admins"
              checked={v.requirePrivilegedTwoStep}
              disabled={!isOwner || save.isPending || (v.requirePrivilegedTwoStep && !v.sessionStrong)}
              onChange={(on) => void flip(on)}
              description={
                !isOwner
                  ? "Only an owner can change this."
                  : v.requirePrivilegedTwoStep && !v.sessionStrong
                    ? "On. To turn it off, sign in with your authenticator app or a text code first."
                    : v.requirePrivilegedTwoStep
                      ? "On (recommended). Owners and admins need an authenticator or text code; an email code alone never reaches the Admin Center."
                      : "Off. Owners and admins sign in like every other role."
              }
            />
          </>
        ) : null}
      </IntegrationBody>
      {dialog}
    </Section>
  );
}
